import React, { useEffect, useRef, useState } from "react";
import useSWR from "swr";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  Textarea,
  VStack,
  Heading,
  HStack,
  useColorMode,
  useColorModeValue,
  useBreakpointValue,
  IconButton,
  Spinner,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import {
  AddIcon,
  AttachmentIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  DeleteIcon,
  HamburgerIcon,
  InfoIcon,
} from "@chakra-ui/icons";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Book, ContentItem } from "../libs/types";
import {
  convertDocument,
  fetchBookById,
  fetchConvertSupportedTypes,
  splitChaptersText,
  updateBook,
  UpdateBookPayload,
} from "../libs/qazbilim-backend";
import LoadingLogo from "./LoadingLogo";
import TableOfContent from "./TableOfContent";
import { revalidateBookCaches } from "../libs/book-swr";

/** Display name from upload: strip last extension (handles "a.b.txt" → "a.b"). */
function chapterTitleFromFilename(filename: string): string {
  const name = filename.trim();
  if (!name) return "";
  const dot = name.lastIndexOf(".");
  if (dot <= 0 || dot === name.length - 1) return name;
  return name.slice(0, dot);
}

const UpdateBook: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { colorMode } = useColorMode();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();

  const [book, setBook] = useState<Book | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);

  const {
    data: loadedBook,
    error: loadError,
    isLoading,
  } = useSWR(
    id ? (["book", id, "edit"] as const) : null,
    ([, bookId]) => fetchBookById(bookId, undefined, true),
    { revalidateOnFocus: false, revalidateOnReconnect: false },
  );

  const loadErrorMessage = loadError
    ? loadError instanceof Error
      ? loadError.message
      : String(loadError)
    : null;
  const [collapsedChapters, setCollapsedChapters] = useState<
    Record<number, boolean>
  >({});
  const [chapterUploadIndex, setChapterUploadIndex] = useState<number | null>(
    null,
  );
  const [pendingChapterUploadIndex, setPendingChapterUploadIndex] = useState<
    number | null
  >(null);
  const [isDraggingContentUpload, setIsDraggingContentUpload] = useState(false);
  const [convertingChapterIndex, setConvertingChapterIndex] = useState<
    number | null
  >(null);
  const [isBulkAddingChapters, setIsBulkAddingChapters] = useState(false);
  const [bulkChaptersUploadZoneOpen, setBulkChaptersUploadZoneOpen] =
    useState(false);
  const [bulkSplitDelimiter, setBulkSplitDelimiter] = useState("");
  const [isTOCOpen, setIsTOCOpen] = useState(false);
  const isMobile = useBreakpointValue({ base: true, md: false });
  const supportedTypesColor = useColorModeValue("gray.600", "gray.400");
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const bulkChaptersFileInputRef = useRef<HTMLInputElement | null>(null);
  const [chapterUploadAccept, setChapterUploadAccept] = useState<string>("");
  const [chapterUploadTypesLabel, setChapterUploadTypesLabel] = useState<
    string | null
  >(null);

  useEffect(() => {
    setMutationError(null);
  }, [id]);

  useEffect(() => {
    if (!id) {
      setBook(null);
      return;
    }
    if (!loadedBook) {
      setBook(null);
      return;
    }
    setBook(loadedBook);
    const initial: Record<number, boolean> = {};
    (loadedBook.content || []).forEach((_, idx) => {
      initial[idx] = true;
    });
    setCollapsedChapters(initial);
  }, [loadedBook, id]);

  useEffect(() => {
    let cancelled = false;
    fetchConvertSupportedTypes()
      .then((d) => {
        if (cancelled) return;
        setChapterUploadAccept(d.accept_markdown);
        setChapterUploadTypesLabel(
          d.markdown_extensions.map((ext) => `.${ext}`).join(", "),
        );
      })
      .catch(() => {
        if (!cancelled) {
          setChapterUploadAccept("");
          setChapterUploadTypesLabel(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleFieldChange = (field: keyof Book, value: string) => {
    if (!book) return;
    setBook({ ...book, [field]: value });
  };

  const handleContentChange = (
    index: number,
    field: keyof ContentItem,
    value: string,
  ) => {
    if (!book) return;
    const updated = [...(book.content || [])];
    updated[index] = {
      ...updated[index],
      [field]: value,
    };
    setBook({ ...book, content: updated });
  };

  const handleRemoveChapter = (index: number) => {
    if (!book) return;
    const currentContent = book.content || [];
    const updatedContent = currentContent.filter((_, i) => i !== index);
    setBook({ ...book, content: updatedContent });

    const nextCollapsed: Record<number, boolean> = {};
    updatedContent.forEach((_, idx) => {
      nextCollapsed[idx] = collapsedChapters[idx] ?? true;
    });
    setCollapsedChapters(nextCollapsed);
  };

  const appendFilesToChapter = async (
    files: File[] | FileList,
    chapterIndex: number,
  ) => {
    if (!book || isBulkAddingChapters) return;
    setConvertingChapterIndex(chapterIndex);
    try {
      for (const file of Array.from(files)) {
        const converted = await convertDocument(file, {
          output_type: "markdown",
        });
        const text = typeof converted === "string" ? converted : "";
        if (!text) continue;
        const titleStem = chapterTitleFromFilename(file.name);
        setBook((prev) => {
          if (!prev) return prev;
          const content = [...(prev.content || [])];
          const existing = content[chapterIndex]?.text || "";
          if (!content[chapterIndex]) {
            content[chapterIndex] = { chapter: "", text: "" };
          }
          const prevChapter = (content[chapterIndex].chapter || "").trim();
          content[chapterIndex] = {
            ...content[chapterIndex],
            chapter: prevChapter || titleStem,
            text: existing ? `${existing}\n\n${text}` : text,
          };
          return { ...prev, content };
        });
      }
    } catch (err) {
      console.error("Chapter upload convert failed", err);
      const first = Array.from(files)[0] as File | undefined;
      toast({
        title: t("updateBook.toastErrorTitle"),
        description: t(
          "createBook.content.convertFailed",
          "Failed to extract text from {{name}}",
          { name: first?.name ?? "" },
        ),
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setConvertingChapterIndex(null);
    }
  };

  const addChaptersFromUploadedFiles = async (files: File[]) => {
    if (!book || files.length === 0) return;
    const startIndex = (book.content || []).length;
    setIsBulkAddingChapters(true);
    const newItems: ContentItem[] = [];
    let skipped = 0;

    const useSplit = bulkSplitDelimiter.trim().length > 0;

    try {
      for (const file of files) {
        try {
          const converted = await convertDocument(file, {
            output_type: "markdown",
          });
          const text = typeof converted === "string" ? converted : "";
          if (!text.trim()) {
            skipped += 1;
            continue;
          }

          if (useSplit) {
            const chapters = await splitChaptersText(text, {
              delimiter: bulkSplitDelimiter.trim(),
            });
            const baseTitle = chapterTitleFromFilename(file.name);
            let addedFromFile = 0;
            chapters.forEach(
              (item: { chapter: string; text: string }, i: number) => {
                const body = (item.text || "").trim();
                if (!body) return;
                const titleRaw = (item.chapter || "").trim();
                const chapter = titleRaw || `${baseTitle} — ${i + 1}`;
                newItems.push({ chapter, text: body });
                addedFromFile += 1;
              },
            );
            if (addedFromFile === 0) {
              skipped += 1;
            }
          } else {
            newItems.push({
              chapter: chapterTitleFromFilename(file.name),
              text,
            });
          }
        } catch {
          skipped += 1;
        }
      }

      if (newItems.length > 0) {
        setBook((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            content: [...(prev.content || []), ...newItems],
          };
        });
        setCollapsedChapters((prev) => {
          const next = { ...prev };
          newItems.forEach((_, i) => {
            next[startIndex + i] = false;
          });
          return next;
        });
      }

      if (newItems.length === 0) {
        toast({
          title: t("updateBook.bulkImportFailedTitle", "No chapters imported"),
          description: t(
            "updateBook.bulkImportFailedDescription",
            "No new chapters were added. Check supported file types and that files contain extractable text.",
          ),
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } else if (skipped > 0) {
        toast({
          title: t(
            "updateBook.bulkImportPartialTitle",
            "Import finished with issues",
          ),
          description: t(
            "updateBook.bulkImportPartialDescription",
            "{{added}} chapters added; {{skipped}} file(s) failed or had no text.",
            { added: newItems.length, skipped },
          ),
          status: "warning",
          duration: 6000,
          isClosable: true,
        });
      } else {
        toast({
          title: t("updateBook.bulkImportSuccessTitle", "Chapters imported"),
          description: t(
            "updateBook.bulkImportSuccessDescription",
            "{{count}} chapters added from files.",
            { count: newItems.length },
          ),
          status: "success",
          duration: 4000,
          isClosable: true,
        });
      }
    } finally {
      setIsBulkAddingChapters(false);
    }
  };

  const handleBulkChaptersFileSelect = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = e.target.files;
    e.target.value = "";
    if (!files?.length) return;
    await addChaptersFromUploadedFiles(Array.from(files));
  };

  const handleContentFileSelect = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = e.target.files;
    if (!files?.length) return;

    if (pendingChapterUploadIndex !== null) {
      const targetIndex = pendingChapterUploadIndex;
      setPendingChapterUploadIndex(null);
      await appendFilesToChapter(files, targetIndex);
      e.target.value = "";
      return;
    }

    e.target.value = "";
  };

  const handleContentUploadDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingContentUpload(true);
  };

  const handleContentUploadDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleContentUploadDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingContentUpload(false);
  };

  const handleChapterUploadDrop = async (
    e: React.DragEvent,
    chapterIndex: number,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingContentUpload(false);
    if (isBulkAddingChapters || !e.dataTransfer.files?.length) return;
    await appendFilesToChapter(e.dataTransfer.files, chapterIndex);
  };

  const handleBulkChaptersUploadDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingContentUpload(false);
    if (isBulkAddingChapters || !e.dataTransfer.files?.length) return;
    await addChaptersFromUploadedFiles(Array.from(e.dataTransfer.files));
  };

  const toggleChapterCollapsed = (index: number) => {
    setCollapsedChapters((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const handleAddChapter = () => {
    if (!book) return;
    const currentContent = book.content || [];
    const nextIndex = currentContent.length;
    const updatedContent = [...currentContent, { chapter: "", text: "" }];
    setBook({ ...book, content: updatedContent });
    setCollapsedChapters((prev) => ({ ...prev, [nextIndex]: false }));
  };

  const handlePreview = () => {
    if (!book) return;
    const draftBook: Book = {
      ...book,
      id: "preview",
      title: book.title || t("createBook.preview.untitled", "Untitled"),
    };
    navigate("/read/preview", {
      state: { previewBook: draftBook, returnTo: `/update-book/${id}` },
    });
  };

  /**
   * Save book. Only Publish/Unpublish change the published flag.
   * @param publishedOverride - true = Publish, false = Unpublish, null = Save Book (keep current status)
   */
  const handleSave = async (publishedOverride: boolean | null) => {
    if (!book || !id) return;

    // Validation: same as CreateBook
    if (!book.author?.trim()) {
      toast({
        title: t("updateBook.toastErrorTitle"),
        description: t(
          "createBook.toast.authorRequired",
          "Please enter or select an author",
        ),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!book.coverUrl) {
      toast({
        title: t("createBook.toast.coverRequiredTitle"),
        description: t("createBook.toast.coverRequiredDescription"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const content = book.content || [];
    if (content.length === 0) {
      toast({
        title: t("updateBook.toastErrorTitle"),
        description: t(
          "updateBook.contentRequired",
          "At least one chapter is required.",
        ),
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    // Normalize content: backend requires non-empty chapter and text per item (same as CreateBook)
    const normalizedContent: ContentItem[] = content.map((item, i) => {
      const raw =
        item && typeof item === "object" ? item : { chapter: "", text: "" };
      return {
        chapter:
          (raw.chapter != null && String(raw.chapter).trim()) ||
          t("createBook.form.chapter", "Chapter") + ` ${i + 1}`,
        text:
          raw.text != null && String(raw.text).trim()
            ? String(raw.text).trim()
            : " ",
      };
    });

    try {
      setIsSaving(true);
      setMutationError(null);

      const payload: UpdateBookPayload = {
        title: book.title,
        author: book.author,
        type: book.type,
        summary: book.summary,
        coverUrl: book.coverUrl,
        language: book.language,
        content: normalizedContent,
        accessType: book.accessType,
      };
      // Only Publish and Unpublish buttons change published; Save Book keeps current status
      if (publishedOverride !== null) {
        payload.published = publishedOverride;
      }

      const updated = await updateBook(id, payload);
      await revalidateBookCaches(id);

      toast({
        title: t("updateBook.toastSuccessTitle"),
        description: t("updateBook.toastSuccessDescription", {
          title: updated.title,
        }),
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      navigate(`/read/${updated.id}`);
    } catch (e) {
      setMutationError(e instanceof Error ? e.message : String(e));
      toast({
        title: t("updateBook.toastErrorTitle"),
        description: t("updateBook.toastErrorDescription"),
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <VStack
        id="update-book-loading"
        spacing={4}
        minH="400px"
        justify="center"
      >
        <LoadingLogo size="xl" />
        <Heading size="md">{t("updateBook.loading")}</Heading>
      </VStack>
    );
  }

  if (!isLoading && (mutationError || loadErrorMessage || !book)) {
    return (
      <VStack id="update-book-error" spacing={4} minH="300px" justify="center">
        <Heading size="md">{t("updateBook.errorTitle")}</Heading>
        <Box>
          {mutationError || loadErrorMessage || t("updateBook.errorFallback")}
        </Box>
        <Button onClick={() => navigate(-1)}>{t("updateBook.back")}</Button>
      </VStack>
    );
  }

  if (!book) {
    return null;
  }

  const isChapterPipelineBusy =
    isBulkAddingChapters || convertingChapterIndex !== null;

  return (
    <Box
      id="update-book-page"
      p={{ base: 0, md: 6 }}
      maxW="container.md"
      mx="auto"
    >
      <VStack align="stretch" spacing={6}>
        <Heading
          id="update-book-title"
          size={{ base: "md", md: "lg" }}
          color={colorMode === "light" ? "gray.800" : "white"}
        >
          {t("updateBook.heading")}
        </Heading>

        <VStack spacing={4} align="stretch">
          <FormControl id="update-book-title-control">
            <FormLabel>{t("createBook.form.title")}</FormLabel>
            <Input
              value={book.title}
              onChange={(e) => handleFieldChange("title", e.target.value)}
            />
          </FormControl>

          <FormControl id="update-book-author-control">
            <FormLabel>{t("createBook.form.author")}</FormLabel>
            <InputGroup>
              <Input
                id="update-book-author-input"
                value={book.author}
                isReadOnly
                cursor="not-allowed"
                bg={colorMode === "light" ? "gray.100" : "whiteAlpha.200"}
                pr="10"
              />
              <InputRightElement width="10">
                <IconButton
                  id="update-book-author-info-button"
                  aria-label={t(
                    "updateBook.authorNotEditableInfo",
                    "Why author cannot be edited",
                  )}
                  icon={<InfoIcon />}
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    toast({
                      title: t(
                        "updateBook.authorNotEditableTitle",
                        "Author cannot be changed",
                      ),
                      description: t(
                        "updateBook.authorNotEditableDescription",
                        "Editing the author is not allowed. Create a new book to use a different author.",
                      ),
                      status: "info",
                      duration: 6000,
                      isClosable: true,
                    })
                  }
                />
              </InputRightElement>
            </InputGroup>
          </FormControl>

          <FormControl id="update-book-summary-control">
            <FormLabel>{t("createBook.form.summary")}</FormLabel>
            <Textarea
              value={book.summary}
              onChange={(e) => handleFieldChange("summary", e.target.value)}
              rows={4}
            />
          </FormControl>
        </VStack>

        <VStack id="update-book-chapters" align="stretch" spacing={3}>
          <HStack justify="space-between" align="center" w="100%">
            <Heading size="sm">{t("updateBook.chaptersHeading")}</Heading>
            <IconButton
              id="read-book-toc-icon-button"
              aria-label={t("readBook.tableOfContents", "Table of Contents")}
              icon={<HamburgerIcon boxSize={4} />}
              size="md"
              isRound
              variant="outline"
              onClick={() => setIsTOCOpen(true)}
            />
          </HStack>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={chapterUploadAccept || undefined}
            style={{ display: "none" }}
            onChange={handleContentFileSelect}
          />
          {(book.content || []).map((item, index) => {
            const isCollapsed = collapsedChapters[index] ?? true;
            return (
              <Box
                key={index}
                id={`update-book-chapter-${index}`}
                borderWidth="1px"
                borderRadius="md"
                p={3}
              >
                <Stack
                  direction="row"
                  align="center"
                  justify="space-between"
                  cursor="pointer"
                  onClick={() => toggleChapterCollapsed(index)}
                >
                  <Heading size="sm">
                    {item.chapter ||
                      t("createBook.preview.untitled", "Untitled")}
                  </Heading>
                  <IconButton
                    aria-label={isCollapsed ? "Expand" : "Collapse"}
                    icon={
                      isCollapsed ? <ChevronRightIcon /> : <ChevronDownIcon />
                    }
                    size="sm"
                    variant="ghost"
                  />
                </Stack>
                {!isCollapsed && (
                  <VStack mt={3} align="stretch" spacing={2}>
                    <Stack direction="row" justify="flex-end" spacing={2}>
                      <IconButton
                        id={`update-book-add-chapter-inline-${index}`}
                        icon={<AddIcon />}
                        aria-label={t(
                          "createBook.form.addChapter",
                          "Add chapter",
                        )}
                        size="sm"
                        variant="outline"
                        isDisabled={isChapterPipelineBusy}
                        onClick={handleAddChapter}
                      />
                      <IconButton
                        id={`update-book-chapter-upload-toggle-${index}`}
                        icon={<AttachmentIcon />}
                        aria-label={t(
                          "createBook.content.uploadIntoChapter",
                          "Upload into this chapter",
                        )}
                        isDisabled={isBulkAddingChapters}
                        onClick={() => {
                          setChapterUploadIndex((prev) =>
                            prev === index ? null : index,
                          );
                        }}
                        size="sm"
                        variant={
                          chapterUploadIndex === index ? "solid" : "outline"
                        }
                      />
                      <IconButton
                        id={`update-book-remove-chapter-${index}`}
                        icon={<DeleteIcon />}
                        colorScheme="red"
                        variant="outline"
                        aria-label={t(
                          "createBook.content.remove",
                          "Remove chapter",
                        )}
                        isDisabled={isChapterPipelineBusy}
                        onClick={() => handleRemoveChapter(index)}
                        size="sm"
                      />
                    </Stack>
                    <FormControl
                      id={`update-book-chapter-title-control-${index}`}
                    >
                      <FormLabel htmlFor={`update-book-chapter-title-${index}`}>
                        {t("createBook.form.chapterTitle")}
                      </FormLabel>
                      <Input
                        id={`update-book-chapter-title-${index}`}
                        value={item.chapter}
                        onChange={(e) =>
                          handleContentChange(index, "chapter", e.target.value)
                        }
                      />
                    </FormControl>
                    {chapterUploadIndex === index && (
                      <Box
                        id={`update-book-chapter-upload-zone-${index}`}
                        borderRadius="md"
                        minHeight="140px"
                        py={3}
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        opacity={isBulkAddingChapters ? 0.5 : 1}
                        pointerEvents={isBulkAddingChapters ? "none" : "auto"}
                        bg={
                          isDraggingContentUpload
                            ? colorMode === "light"
                              ? "blue.50"
                              : "rgba(40, 50, 70, 0.6)"
                            : colorMode === "light"
                              ? "rgba(240, 240, 240, 0.6)"
                              : "rgba(30, 35, 45, 0.6)"
                        }
                        _hover={{
                          bg:
                            colorMode === "light"
                              ? "rgba(235, 235, 235, 0.7)"
                              : "rgba(35, 40, 50, 0.7)",
                          cursor: "pointer",
                        }}
                        boxShadow={
                          isDraggingContentUpload
                            ? "0 0 0 2px var(--chakra-colors-blue-500)"
                            : "sm"
                        }
                        border={isDraggingContentUpload ? "2px dashed" : "none"}
                        borderColor={
                          isDraggingContentUpload ? "blue.500" : undefined
                        }
                        transition="all 0.2s"
                        onClick={() => {
                          setPendingChapterUploadIndex(index);
                          fileInputRef.current?.click();
                        }}
                        onDragEnter={handleContentUploadDragEnter}
                        onDragOver={handleContentUploadDragOver}
                        onDragLeave={handleContentUploadDragLeave}
                        onDrop={(e) => handleChapterUploadDrop(e, index)}
                      >
                        <VStack
                          spacing={2}
                          px={{ base: 4, md: 2 }}
                          textAlign="center"
                        >
                          <AttachmentIcon boxSize={8} />
                          <Heading size="xs">
                            {isDraggingContentUpload
                              ? t("createBook.content.dropHere", "Drop here")
                              : t(
                                  "createBook.content.dropOrClick",
                                  "Drop files here to upload into this chapter",
                                )}
                          </Heading>
                          {chapterUploadTypesLabel ? (
                            <Text
                              id={`update-book-chapter-upload-zone-${index}-supported-types`}
                              fontSize="xs"
                              color={supportedTypesColor}
                              lineHeight="short"
                              wordBreak="break-word"
                            >
                              {t(
                                "createBook.content.chapterUploadSupportedTypes",
                                "Supported types: {{types}}",
                                { types: chapterUploadTypesLabel },
                              )}
                            </Text>
                          ) : null}
                        </VStack>
                      </Box>
                    )}
                    <FormControl position="relative">
                      <FormLabel>
                        {t("createBook.form.chapterContent")}
                      </FormLabel>
                      <Box position="relative">
                        <Textarea
                          value={item.text}
                          onChange={(e) =>
                            handleContentChange(index, "text", e.target.value)
                          }
                          rows={6}
                        />
                        {convertingChapterIndex === index && (
                          <Box
                            position="absolute"
                            inset={0}
                            borderRadius="md"
                            bg={
                              colorMode === "light"
                                ? "whiteAlpha.900"
                                : "blackAlpha.800"
                            }
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            zIndex={1}
                          >
                            <VStack spacing={2}>
                              <Spinner size="lg" />
                              <Heading size="xs" as="span">
                                {t(
                                  "createBook.form.processingDocument",
                                  "Processing document…",
                                )}
                              </Heading>
                            </VStack>
                          </Box>
                        )}
                      </Box>
                    </FormControl>
                  </VStack>
                )}
              </Box>
            );
          })}
          <HStack spacing={2} alignSelf="flex-start" flexWrap="wrap">
            <Button
              id="update-book-add-chapter-button"
              size="sm"
              variant="outline"
              leftIcon={<AddIcon />}
              isDisabled={isChapterPipelineBusy}
              onClick={handleAddChapter}
            >
              {t("createBook.form.addChapter")}
            </Button>
            <Button
              id="update-book-add-chapters-from-files-button"
              size="sm"
              variant={bulkChaptersUploadZoneOpen ? "solid" : "outline"}
              colorScheme={bulkChaptersUploadZoneOpen ? "blue" : undefined}
              leftIcon={<AttachmentIcon />}
              isDisabled={isChapterPipelineBusy}
              aria-expanded={bulkChaptersUploadZoneOpen}
              onClick={() => {
                setBulkChaptersUploadZoneOpen((prev) => {
                  const next = !prev;
                  if (next) setChapterUploadIndex(null);
                  return next;
                });
              }}
            >
              {t("updateBook.addChaptersFromFiles", "Add chapters from files")}
            </Button>
          </HStack>

          {bulkChaptersUploadZoneOpen && (
            <Box id="content-upload-area" alignSelf="stretch" mt={2}>
              <Text
                fontSize="sm"
                color={supportedTypesColor}
                lineHeight="short"
                mb={3}
              >
                {t(
                  "updateBook.bulkImportIntro",
                  "Upload one or more documents. Text is extracted from each file. You can leave “Split by” empty to get one chapter per file, or split into multiple chapters wherever a whole line matches the text you enter below (for example “Chapter” or “***”).",
                )}
              </Text>
              <VStack align="stretch" spacing={3} mb={3}>
                <FormControl>
                  <HStack spacing={1} align="center" mb={1}>
                    <FormLabel
                      htmlFor="update-book-bulk-split-delimiter"
                      mb={0}
                    >
                      {t(
                        "updateBook.bulkSplitDelimiterLabel",
                        "Split by (line)",
                      )}
                    </FormLabel>
                    <IconButton
                      id="update-book-bulk-split-delimiter-info"
                      aria-label={t(
                        "updateBook.bulkSplitDelimiterInfoAria",
                        "More about splitting by line",
                      )}
                      icon={<InfoIcon />}
                      size="sm"
                      variant="ghost"
                      isDisabled={isChapterPipelineBusy}
                      onClick={() =>
                        toast({
                          title: t(
                            "updateBook.bulkSplitDelimiterLabel",
                            "Split by (line)",
                          ),
                          description: t(
                            "updateBook.bulkSplitDelimiterHelper",
                            "If set, each uploaded file is split into multiple chapters wherever a line equals this text (spaces at the ends of the line are ignored). Leave empty to keep one chapter per file.",
                          ),
                          status: "info",
                          duration: 8000,
                          isClosable: true,
                        })
                      }
                    />
                  </HStack>
                  <Input
                    id="update-book-bulk-split-delimiter"
                    size="sm"
                    value={bulkSplitDelimiter}
                    onChange={(e) => setBulkSplitDelimiter(e.target.value)}
                    placeholder={t(
                      "updateBook.bulkSplitDelimiterPlaceholder",
                      "e.g. ***",
                    )}
                    isDisabled={isChapterPipelineBusy}
                  />
                </FormControl>
              </VStack>
              <input
                ref={bulkChaptersFileInputRef}
                type="file"
                multiple
                accept={chapterUploadAccept || undefined}
                style={{ display: "none" }}
                onChange={handleBulkChaptersFileSelect}
              />
              <Box position="relative">
                <Box
                  id="content-upload-zone"
                  borderRadius="md"
                  height="180px"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  bg={
                    isDraggingContentUpload
                      ? colorMode === "light"
                        ? "blue.50"
                        : "rgba(40, 50, 70, 0.6)"
                      : colorMode === "light"
                        ? "rgba(240, 240, 240, 0.6)"
                        : "rgba(30, 35, 45, 0.6)"
                  }
                  _hover={{
                    bg:
                      colorMode === "light"
                        ? "rgba(235, 235, 235, 0.7)"
                        : "rgba(35, 40, 50, 0.7)",
                    cursor: isBulkAddingChapters ? "default" : "pointer",
                  }}
                  onClick={() => {
                    if (!isBulkAddingChapters) {
                      bulkChaptersFileInputRef.current?.click();
                    }
                  }}
                  boxShadow={
                    isDraggingContentUpload
                      ? "0 0 0 2px var(--chakra-colors-blue-500)"
                      : "sm"
                  }
                  border={isDraggingContentUpload ? "2px dashed" : "none"}
                  borderColor={isDraggingContentUpload ? "blue.500" : undefined}
                  transition="all 0.2s"
                  opacity={isBulkAddingChapters ? 0.85 : 1}
                  pointerEvents={isBulkAddingChapters ? "none" : "auto"}
                  onDragEnter={handleContentUploadDragEnter}
                  onDragOver={handleContentUploadDragOver}
                  onDragLeave={handleContentUploadDragLeave}
                  onDrop={handleBulkChaptersUploadDrop}
                >
                  <VStack
                    id="content-upload-zone-text"
                    spacing={2}
                    px={{ base: 4, md: 2 }}
                    textAlign="center"
                  >
                    <AttachmentIcon boxSize={10} />
                    <Text
                      id="content-upload-zone-main-text"
                      fontWeight="medium"
                    >
                      {isDraggingContentUpload
                        ? t("createBook.content.dropHere", "Drop here")
                        : t(
                            "createBook.content.dropOrClick",
                            "Drop files here or click to upload",
                          )}
                    </Text>
                    {chapterUploadTypesLabel ? (
                      <Text
                        id="content-upload-zone-hint"
                        fontSize="xs"
                        color={supportedTypesColor}
                        lineHeight="short"
                        wordBreak="break-word"
                      >
                        {t(
                          "createBook.content.chapterUploadSupportedTypes",
                          "Supported types: {{types}}",
                          { types: chapterUploadTypesLabel },
                        )}
                      </Text>
                    ) : null}
                  </VStack>
                </Box>
                {isBulkAddingChapters && (
                  <Box
                    position="absolute"
                    inset={0}
                    borderRadius="md"
                    bg={
                      colorMode === "light"
                        ? "whiteAlpha.900"
                        : "blackAlpha.800"
                    }
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    zIndex={1}
                  >
                    <VStack spacing={2}>
                      <Spinner size="lg" />
                      <Text fontWeight="medium" fontSize="sm">
                        {t("updateBook.bulkImportLoading", "Importing…")}
                      </Text>
                    </VStack>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </VStack>

        <TableOfContent
          isOpen={isTOCOpen}
          onClose={() => setIsTOCOpen(false)}
          contentItems={book.content || []}
          currentChapterIndex={-1}
          onChapterSelect={(index) => {
            setCollapsedChapters((prev) => ({ ...prev, [index]: false }));
            setIsTOCOpen(false);
            setTimeout(
              () =>
                document
                  .getElementById(`update-book-chapter-${index}`)
                  ?.scrollIntoView({ behavior: "smooth" }),
              100,
            );
          }}
          isMobile={isMobile}
          t={t}
        />

        <Stack
          direction="row"
          justify="flex-end"
          spacing={4}
          wrap="wrap"
          gap={2}
        >
          <Button
            id="preview-book-button"
            variant="outline"
            type="button"
            whiteSpace="normal"
            minW={{ base: "min-content", md: "auto" }}
            onClick={handlePreview}
          >
            {t("createBook.form.preview", "Preview")}
          </Button>
          <Button
            id="update-book-save-button"
            variant="outline"
            type="button"
            whiteSpace="normal"
            minW={{ base: "min-content", md: "auto" }}
            onClick={() => handleSave(null)}
            isLoading={isSaving}
            loadingText={t("createBook.form.submitting", "Submitting")}
          >
            {t("createBook.form.saveBook", "Save Book")}
          </Button>
          <Button
            id="update-book-publish-button"
            colorScheme="blue"
            type="button"
            whiteSpace="normal"
            minW={{ base: "min-content", md: "auto" }}
            onClick={() => handleSave(book.published === true ? false : true)}
            isLoading={isSaving}
            loadingText={t("createBook.form.submitting", "Submitting")}
          >
            {book.published === true
              ? t("updateBook.unpublish", "Unpublish")
              : t("createBook.form.publish", "Publish")}
          </Button>
        </Stack>
      </VStack>
    </Box>
  );
};

export default UpdateBook;
